import { Chess, Swal, createViewerBoard, renderMoveList, setViewerPosition } from './chess-ui.js';
import { parsePgnAsync, readPgnFile as readPgnFileSafely } from './pgn-import-client.js';
import { saveNotebookTransfer } from './game-notebook-transfer.js';

const pgnRoot = document.querySelector('[data-pgn-root]');
const dropZone = document.querySelector('[data-drop-zone]');

if (dropZone && pgnRoot) {
  const stockfishUrl = pgnRoot.dataset.stockfishUrl || '/vendor/stockfish/stockfish-18-lite-single.js';
  const fileInput = pgnRoot.querySelector('[data-pgn-file]');
  const pgnInput = pgnRoot.querySelector('[data-pgn-input]');
  const viewer = pgnRoot.querySelector('[data-pgn-viewer]');
  const changePgnButton = pgnRoot.querySelector('[data-change-pgn]');
  const movesElement = pgnRoot.querySelector('[data-pgn-moves]');
  const metaElement = pgnRoot.querySelector('[data-pgn-meta]');
  const indicator = pgnRoot.querySelector('[data-ply-indicator]');
  const gameSelector = pgnRoot.querySelector('[data-game-selector]');
  const gameCount = pgnRoot.querySelector('[data-game-count]');
  const moveCount = pgnRoot.querySelector('[data-move-count]');
  const previousGameButton = pgnRoot.querySelector('[data-prev-game]');
  const nextGameButton = pgnRoot.querySelector('[data-next-game]');
  const analysisToggle = pgnRoot.querySelector('[data-toggle-pgn-analysis]');
  const analysisDepth = pgnRoot.querySelector('[data-pgn-engine-depth]');
  const analysisStatus = pgnRoot.querySelector('[data-pgn-engine-status]');
  const analysisScore = pgnRoot.querySelector('[data-pgn-engine-score]');
  const analysisPosition = pgnRoot.querySelector('[data-pgn-engine-position]');
  const analysisLines = pgnRoot.querySelector('[data-pgn-engine-lines]');
  const firstMoveButton = pgnRoot.querySelector('[data-first-move]');
  const previousMoveButton = pgnRoot.querySelector('[data-prev-move]');
  const nextMoveButton = pgnRoot.querySelector('[data-next-move]');
  const lastMoveButton = pgnRoot.querySelector('[data-last-move]');
  const readingState = pgnRoot.querySelector('[data-pgn-reading-state]');
  const readingTitle = pgnRoot.querySelector('[data-pgn-reading-title]');
  const readingProgress = pgnRoot.querySelector('[data-pgn-reading-progress]');
  const picker = pgnRoot.querySelector('[data-pgn-game-picker]');
  const pickerSearch = pgnRoot.querySelector('[data-pgn-picker-search]');
  const pickerList = pgnRoot.querySelector('[data-pgn-picker-list]');
  const pickerPage = pgnRoot.querySelector('[data-pgn-picker-page]');
  const pickerPrevious = pgnRoot.querySelector('[data-pgn-picker-prev]');
  const pickerNext = pgnRoot.querySelector('[data-pgn-picker-next]');
  const pickerSummary = pgnRoot.querySelector('[data-pgn-picker-summary]');

  let ground = null;
  let orientation = 'white';
  let games = [];
  let selectedGameIndex = -1;
  let positions = [];
  let moves = [];
  let currentPly = 0;
  let pickerPageNumber = 1;
  const pickerPageSize = 40;

  let engine = null;
  let engineReady = false;
  let engineEnabled = false;
  let analysisTimer = 0;
  let analysisSequence = 0;
  let pendingAnalysis = null;
  let activeAnalysis = null;
  const engineLines = new Map();

  pgnRoot.querySelector('[data-select-pgn]').addEventListener('click', event => {
    event.stopPropagation();
    fileInput.click();
  });
  dropZone.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => readFile(fileInput.files[0]));
  pgnRoot.querySelector('[data-load-pgn]').addEventListener('click', () => loadCollection(pgnInput.value));
  firstMoveButton.addEventListener('click', () => showPly(0));
  previousMoveButton.addEventListener('click', () => showPly(currentPly - 1));
  nextMoveButton.addEventListener('click', () => showPly(currentPly + 1));
  lastMoveButton.addEventListener('click', () => showPly(moves.length));
  pgnRoot.querySelector('[data-flip-pgn]').addEventListener('click', flipBoard);
  previousGameButton.addEventListener('click', () => selectGame(selectedGameIndex - 1));
  nextGameButton.addEventListener('click', () => selectGame(selectedGameIndex + 1));
  gameSelector.addEventListener('change', () => selectGame(Number(gameSelector.value)));
  analysisToggle.addEventListener('click', toggleAnalysis);
  analysisDepth.addEventListener('change', scheduleAnalysis);
  pgnRoot.querySelector('[data-save-pgn-notebook]')?.addEventListener('click', saveSelectedGameToNotebook);
  pickerSearch?.addEventListener('input', () => {
    pickerPageNumber = 1;
    renderGamePicker();
  });
  pickerPrevious?.addEventListener('click', () => {
    pickerPageNumber = Math.max(1, pickerPageNumber - 1);
    renderGamePicker();
  });
  pickerNext?.addEventListener('click', () => {
    pickerPageNumber++;
    renderGamePicker();
  });

  changePgnButton?.addEventListener('click', () => {
    stopAnalysis();
    document.body.classList.remove('pgn-viewer-active');
    viewer.classList.add('hidden');
    fileInput.value = '';
    pgnRoot.querySelector('[data-pgn-source-area]')?.focus?.();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  document.addEventListener('keydown', event => {
    if (event.target instanceof HTMLInputElement
      || event.target instanceof HTMLTextAreaElement
      || event.target instanceof HTMLSelectElement) return;
    if (event.key === 'ArrowLeft') showPly(currentPly - 1);
    if (event.key === 'ArrowRight') showPly(currentPly + 1);
    if (event.key === 'Home') showPly(0);
    if (event.key === 'End') showPly(moves.length);
    if (event.key === 'PageUp') selectGame(selectedGameIndex - 1);
    if (event.key === 'PageDown') selectGame(selectedGameIndex + 1);
  });

  window.addEventListener('pagehide', () => {
    clearTimeout(analysisTimer);
    engine?.postMessage('stop');
    engine?.terminate();
  });

  for (const eventName of ['dragenter', 'dragover']) {
    dropZone.addEventListener(eventName, event => {
      event.preventDefault();
      dropZone.classList.add('dragging');
    });
  }
  for (const eventName of ['dragleave', 'drop']) {
    dropZone.addEventListener(eventName, event => {
      event.preventDefault();
      dropZone.classList.remove('dragging');
    });
  }
  dropZone.addEventListener('drop', event => readFile(event.dataTransfer.files[0]));

  async function readFile(file) {
    if (!file) return;
    setReadingState(true, file.name);
    try {
      const text = await readPgnFileSafely(file);
      pgnInput.value = text;
      await loadCollection(text, file.name);
    } catch (error) {
      setReadingState(false);
      await Swal.fire({
        icon: 'error',
        title: error?.code === 'file_too_large' ? 'فایل بیش از حد بزرگ است' : 'فایل خوانده نشد',
        text: error?.message || 'مرورگر نتوانست فایل PGN را بخواند.',
        confirmButtonText: 'باشه'
      });
    }
  }

  async function loadCollection(pgn, fileName = '') {
    if (!pgn.trim()) return;
    setReadingState(true, fileName);
    let result;
    try {
      result = await parsePgnAsync(pgn, {
        onProgress: (completed, total) => {
          readingProgress.textContent = total > 0
            ? `${completed.toLocaleString('fa-IR')} از ${total.toLocaleString('fa-IR')} بازی بررسی شد.`
            : 'در حال شناسایی بازی‌های فایل…';
        }
      });
    } catch (error) {
      setReadingState(false);
      await Swal.fire({
        icon: 'error',
        title: error?.code === 'file_too_large' ? 'فایل بیش از حد بزرگ است' : 'PGN خوانده نشد',
        text: error?.message || 'مرورگر نتوانست مجموعه را بخواند.',
        confirmButtonText: 'باشه'
      });
      return;
    }
    setReadingState(false);
    if (result.games.length === 0) {
      const details = result.errors[0]?.message ?? 'هیچ بازی قابل‌خواندنی در فایل پیدا نشد.';
      await Swal.fire({ icon: 'error', title: 'PGN معتبر نیست', text: details, confirmButtonText: 'باشه' });
      return;
    }

    games = result.games;
    populateGameSelector(fileName);
    selectedGameIndex = -1;
    openGamePicker();

    if (result.errors.length > 0) {
      void Swal.fire({
        toast: true,
        position: 'bottom-start',
        icon: 'warning',
        title: `${result.games.length} بازی بارگذاری شد و ${result.errors.length} بازی نامعتبر نادیده گرفته شد.`,
        showConfirmButton: false,
        timer: 4200
      });
    }
  }

  function populateGameSelector(fileName) {
    gameSelector.replaceChildren();
    games.forEach((item, index) => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = item.title;
      gameSelector.append(option);
    });
    const fileLabel = fileName ? ` • ${fileName}` : '';
    gameCount.textContent = `${toPersianNumber(games.length)} بازی${fileLabel}`;
  }

  function selectGame(index, force = false) {
    if (games.length === 0) return;
    const bounded = Math.max(0, Math.min(Number(index), games.length - 1));
    if (!force && bounded === selectedGameIndex) return;

    selectedGameIndex = bounded;
    const selected = games[selectedGameIndex];
    moves = selected.moves;
    positions = selected.positions;
    currentPly = 0;
    gameSelector.value = String(selectedGameIndex);
    previousGameButton.disabled = selectedGameIndex <= 0;
    nextGameButton.disabled = selectedGameIndex >= games.length - 1;
    moveCount.textContent = `${toPersianNumber(Math.ceil(moves.length / 2))} حرکت`;

    renderMetadata(selected.headers);
    renderBoardSummary(selected.headers);
    if (!ground) {
      ground = createViewerBoard(pgnRoot.querySelector('#pgnBoard'), positions[0], orientation);
    } else {
      ground.set({ orientation });
    }
    showPly(0, true);
  }

  function openGamePicker() {
    pickerPageNumber = 1;
    pickerSearch.value = '';
    pickerSummary.textContent = `${games.length.toLocaleString('fa-IR')} بازی خوانده شد؛ هیچ بازی به‌صورت پیش‌فرض انتخاب نمی‌شود.`;
    renderGamePicker();
    picker.showModal();
  }

  function renderGamePicker() {
    const term = pickerSearch.value.trim().toLocaleLowerCase('fa');
    const filtered = games.filter(item => {
      if (!term) return true;
      const headers = item.headers || {};
      return [item.title, headers.White, headers.Black, headers.Event, headers.Site, headers.Result]
        .some(value => String(value || '').toLocaleLowerCase('fa').includes(term));
    });
    const pageCount = Math.max(1, Math.ceil(filtered.length / pickerPageSize));
    pickerPageNumber = Math.min(pickerPageNumber, pageCount);
    const start = (pickerPageNumber - 1) * pickerPageSize;
    pickerList.replaceChildren();
    for (const item of filtered.slice(start, start + pickerPageSize)) {
      const button = document.createElement('button');
      button.type = 'button';
      const headers = item.headers || {};
      button.innerHTML = `<span>♟</span><div><strong></strong><small></small></div><em></em>`;
      button.querySelector('strong').textContent = item.title;
      button.querySelector('small').textContent = [headers.Event, headers.Date, headers.Site].filter(Boolean).join(' • ') || 'بدون اطلاعات تکمیلی';
      button.querySelector('em').textContent = `${Math.ceil(item.moves.length / 2).toLocaleString('fa-IR')} حرکت`;
      button.addEventListener('click', () => {
        picker.close();
        document.body.classList.add('pgn-viewer-active');
        viewer.classList.remove('hidden');
        selectGame(games.indexOf(item), true);
        requestAnimationFrame(() => {
          ground?.redrawAll();
          if (matchMedia('(max-width: 900px)').matches) {
            viewer.scrollIntoView({
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'start'
            });
          }
        });
      });
      pickerList.append(button);
    }
    if (filtered.length === 0) {
      const empty = document.createElement('p');
      empty.textContent = 'بازی‌ای با این جستجو پیدا نشد.';
      pickerList.append(empty);
    }
    pickerPage.textContent = `صفحه ${pickerPageNumber.toLocaleString('fa-IR')} از ${pageCount.toLocaleString('fa-IR')}`;
    pickerPrevious.disabled = pickerPageNumber <= 1;
    pickerNext.disabled = pickerPageNumber >= pageCount;
  }

  function setReadingState(visible, fileName = '') {
    readingState.hidden = !visible;
    readingTitle.textContent = fileName ? `در حال خواندن «${fileName}»…` : 'در حال خواندن بازی‌ها…';
    readingProgress.textContent = 'فایل در یک پردازش جداگانه بررسی می‌شود؛ صفحه و مرورگر پاسخ‌گو می‌ماند.';
    pgnRoot.querySelector('[data-load-pgn]').disabled = visible;
    pgnRoot.querySelector('[data-select-pgn]').disabled = visible;
  }

  function saveSelectedGameToNotebook() {
    const selected = games[selectedGameIndex];
    if (!selected) return;
    saveNotebookTransfer({
      kind: 'pgn',
      pgn: selected.pgn,
      title: selected.title
    });
    const url = new URL(pgnRoot.dataset.notebookEditUrl, location.origin);
    url.searchParams.set('method', 'pgn');
    url.searchParams.set('import', 'pgn');
    location.href = url.toString();
  }

  function showPly(ply, force = false) {
    if (!ground || positions.length === 0) return;
    const bounded = Math.max(0, Math.min(Number(ply), moves.length));
    if (!force && bounded === currentPly) return;
    currentPly = bounded;
    const move = currentPly > 0 ? moves[currentPly - 1] : null;
    setViewerPosition(ground, positions[currentPly], move ? [move.from, move.to] : undefined);
    renderMoveList(movesElement, moves, currentPly, showPly);
    indicator.textContent = currentPly === 0
      ? 'شروع بازی'
      : `حرکت ${toPersianNumber(Math.ceil(currentPly / 2))} ${currentPly % 2 === 0 ? 'سیاه' : 'سفید'}`;
    analysisPosition.textContent = currentPly === 0
      ? 'آنالیز پوزیسیون شروع'
      : `آنالیز حرکت ${toPersianNumber(Math.ceil(currentPly / 2))} ${currentPly % 2 ? 'سفید' : 'سیاه'}`;
    firstMoveButton.disabled = currentPly === 0;
    previousMoveButton.disabled = currentPly === 0;
    nextMoveButton.disabled = currentPly >= moves.length;
    lastMoveButton.disabled = currentPly >= moves.length;
    scheduleAnalysis();
  }

  function flipBoard() {
    orientation = orientation === 'white' ? 'black' : 'white';
    ground?.set({ orientation });
  }

  function renderMetadata(headers) {
    const labels = {
      Event: 'رویداد',
      Round: 'دور',
      Date: 'تاریخ',
      Site: 'محل',
      ECO: 'ECO',
      Opening: 'گشایش',
      Result: 'نتیجه'
    };
    metaElement.replaceChildren();
    for (const [key, labelText] of Object.entries(labels)) {
      if (!headers[key]) continue;
      const item = document.createElement('div');
      item.className = 'meta-item';
      const label = document.createElement('span');
      label.textContent = labelText;
      const value = document.createElement('strong');
      value.textContent = headers[key];
      item.append(label, value);
      metaElement.append(item);
    }
  }

  function renderBoardSummary(headers) {
    setText('[data-pgn-white-name]', headers.White || 'سفید');
    setText('[data-pgn-black-name]', headers.Black || 'سیاه');
    setText('[data-pgn-white-rating]', ratingText(headers.WhiteElo));
    setText('[data-pgn-black-rating]', ratingText(headers.BlackElo));
    setText('[data-pgn-event]', headers.Event || headers.Site || 'PGN');
    setText('[data-pgn-result]', headers.Result || '*');
  }

  function setText(selector, value) {
    const element = pgnRoot.querySelector(selector);
    if (element) element.textContent = value;
  }

  function toggleAnalysis() {
    engineEnabled = !engineEnabled;
    analysisToggle.classList.toggle('active', engineEnabled);
    analysisToggle.textContent = engineEnabled ? 'توقف آنالیز' : 'فعال‌سازی آنالیز';
    if (!engineEnabled) {
      stopAnalysis();
      analysisStatus.textContent = 'آنالیز خاموش است';
      analysisScore.textContent = '—';
      renderEnginePlaceholder('برای بررسی حرکت‌ها، آنالیز را فعال کن.');
      return;
    }

    ensureEngine();
    analysisStatus.textContent = engineReady ? 'در حال آماده‌سازی پوزیسیون…' : 'در حال بارگذاری موتور…';
    scheduleAnalysis(true);
  }

  function ensureEngine() {
    if (engine) return;
    engine = new Worker(stockfishUrl);
    engine.addEventListener('message', event => handleEngineMessage(String(event.data)));
    engine.addEventListener('error', () => {
      engineReady = false;
      engineEnabled = false;
      analysisToggle.classList.remove('active');
      analysisToggle.textContent = 'تلاش دوباره';
      analysisStatus.textContent = 'موتور آنالیز بارگذاری نشد';
      renderEnginePlaceholder('فایل Stockfish در مرورگر بارگذاری نشد.');
    });
    engine.postMessage('uci');
  }

  function handleEngineMessage(message) {
    if (message === 'uciok') {
      engine.postMessage('setoption name MultiPV value 3');
      engine.postMessage('isready');
      return;
    }

    if (message === 'readyok') {
      engineReady = true;
      if (engineEnabled && pendingAnalysis) startPendingAnalysis();
      return;
    }

    if (!engineEnabled || !activeAnalysis) return;
    if (message.startsWith('bestmove ')) {
      analysisStatus.textContent = `آنالیز کامل شد • عمق ${activeAnalysis.depth}`;
      return;
    }
    if (!message.startsWith('info ') || !message.includes(' pv ')) return;

    const multipv = Number(matchValue(message, / multipv (\d+)/) ?? 1);
    const depth = Number(matchValue(message, / depth (\d+)/) ?? 0);
    const cp = matchValue(message, / score cp (-?\d+)/);
    const mate = matchValue(message, / score mate (-?\d+)/);
    const pv = matchValue(message, / pv (.+)$/);
    if (!pv) return;

    const score = mate !== null
      ? { type: 'mate', value: Number(mate) }
      : { type: 'cp', value: Number(cp ?? 0) };
    engineLines.set(multipv, { depth, score, pv });
    renderEngineLines();
  }

  function scheduleAnalysis(immediate = false) {
    if (!engineEnabled) return;
    clearTimeout(analysisTimer);
    analysisTimer = window.setTimeout(queueAnalysis, immediate ? 0 : 140);
  }

  function queueAnalysis() {
    if (!engineEnabled || positions.length === 0) return;
    ensureEngine();
    const fen = positions[currentPly];
    pendingAnalysis = {
      sequence: ++analysisSequence,
      fen,
      ply: currentPly,
      depth: Number(analysisDepth.value)
    };
    activeAnalysis = null;
    engineLines.clear();
    analysisScore.textContent = '…';
    analysisStatus.textContent = engineReady ? 'در حال تغییر پوزیسیون…' : 'در حال بارگذاری موتور…';
    renderEnginePlaceholder('موتور در حال محاسبه بهترین ادامه‌هاست…');

    if (!engineReady) return;
    // Wait for readyok after stop. This acts as a UCI barrier and prevents old
    // analysis output from corrupting or freezing navigation after rapid clicks.
    engine.postMessage('stop');
    engine.postMessage('isready');
  }

  function startPendingAnalysis() {
    if (!engineEnabled || !pendingAnalysis) return;
    activeAnalysis = pendingAnalysis;
    pendingAnalysis = null;
    engineLines.clear();
    analysisStatus.textContent = `در حال آنالیز • عمق ${activeAnalysis.depth}`;
    engine.postMessage(`position fen ${activeAnalysis.fen}`);
    engine.postMessage(`go depth ${activeAnalysis.depth}`);
  }

  function stopAnalysis() {
    clearTimeout(analysisTimer);
    pendingAnalysis = null;
    activeAnalysis = null;
    engineLines.clear();
    engine?.postMessage('stop');
  }

  function renderEngineLines() {
    if (!activeAnalysis) return;
    analysisLines.replaceChildren();
    const position = new Chess(activeAnalysis.fen);
    const sorted = [...engineLines.entries()].sort((a, b) => a[0] - b[0]);
    for (const [, line] of sorted) {
      const item = document.createElement('div');
      item.className = 'pgn-engine-line';
      const score = document.createElement('strong');
      score.textContent = formatEvaluation(line.score, position.turn());
      const continuation = document.createElement('span');
      continuation.textContent = pvToSan(line.pv, activeAnalysis.fen);
      continuation.title = continuation.textContent;
      item.append(score, continuation);
      analysisLines.append(item);
    }

    const primary = engineLines.get(1);
    if (primary) {
      analysisScore.textContent = formatEvaluation(primary.score, position.turn());
      analysisStatus.textContent = `عمق ${primary.depth} • سه ادامه برتر`;
    }
  }

  function renderEnginePlaceholder(text) {
    analysisLines.replaceChildren();
    const placeholder = document.createElement('div');
    placeholder.className = 'engine-placeholder';
    placeholder.textContent = text;
    analysisLines.append(placeholder);
  }
}

function ratingText(value) {
  return value ? `ریتینگ ${value}` : '';
}

function toPersianNumber(value) {
  return new Intl.NumberFormat('fa-IR', { useGrouping: false }).format(Number(value) || 0);
}

function matchValue(text, expression) {
  return text.match(expression)?.[1] ?? null;
}

function formatEvaluation(score, turn) {
  const whitePerspective = turn === 'w' ? score.value : -score.value;
  if (score.type === 'mate') return `${whitePerspective < 0 ? '-' : ''}M${Math.abs(whitePerspective)}`;
  return `${whitePerspective >= 0 ? '+' : ''}${(whitePerspective / 100).toFixed(2)}`;
}

function pvToSan(pv, fen) {
  const replay = new Chess(fen);
  const sans = [];
  for (const uci of pv.split(' ').slice(0, 14)) {
    try {
      const move = replay.move({
        from: uci.slice(0, 2),
        to: uci.slice(2, 4),
        promotion: uci[4]
      });
      if (!move) break;
      sans.push(move.san);
    } catch {
      break;
    }
  }
  return sans.join(' ');
}
